import { useState } from "react";
import { CheckCircle, PaperPlaneTilt, SpinnerGap } from "@phosphor-icons/react";
import { Link } from "react-router";
import { FormError, HoneypotField } from "@/components/ui/FormField";
import { Turnstile } from "@/components/ui/Turnstile";
import {
  failureReason,
  FormSubmitError,
  submitNewsletter,
  type NewsletterTopic,
} from "@/lib/forms";
import { track } from "@/lib/track";

/**
 * Newsletter signup. Posts to our own edge function, which forwards to
 * listmonk — the browser never sees a list UUID.
 *
 * listmonk sends a confirmation email and only mails people who click it, so
 * the success copy has to tell the visitor to go and look for it. A signup
 * that stops at "thanks" leaves people believing they subscribed when they
 * did not.
 */

interface NewsletterSignupProps {
  /** `footer` is the compact single-row form; `card` is the standalone block. */
  variant?: "footer" | "card";
  topics?: NewsletterTopic[];
  className?: string;
}

export function NewsletterSignup({
  variant = "footer",
  topics = ["weekly"],
  className = "",
}: NewsletterSignupProps) {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);
  const [status, setStatus] = useState<"idle" | "submitting" | "done">("idle");
  const [needsConfirmation, setNeedsConfirmation] = useState(true);
  const [error, setError] = useState("");

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (status === "submitting") return;

    setStatus("submitting");
    setError("");
    // Event properties never carry the address — only where the form sits.
    track("newsletter_subscribe_submitted", { variant });

    try {
      const result = await submitNewsletter({
        email,
        topics,
        companyWebsite: honeypot,
        turnstileToken,
      });
      setNeedsConfirmation(result.hasOptin);
      setStatus("done");
      track("newsletter_subscribe_succeeded", { variant, needs_confirmation: result.hasOptin });
    } catch (err) {
      track("newsletter_subscribe_failed", { variant, reason: failureReason(err) });
      setError(
        err instanceof FormSubmitError ? err.message : "Something went wrong. Please try again."
      );
      setStatus("idle");
      // Tokens are single-use — a retry needs a fresh one.
      setTurnstileToken("");
      setResetSignal((n) => n + 1);
    }
  }

  if (status === "done") {
    return (
      <div role="status" className={`flex items-start gap-3 ${className}`}>
        <CheckCircle size={20} weight="duotone" className="mt-0.5 shrink-0 text-signal" />
        <p className="text-sm leading-relaxed text-muted-foreground">
          {needsConfirmation ? (
            <>
              Almost there — we've sent a confirmation link to{" "}
              <span className="text-foreground">{email}</span>. Click it and you're subscribed.
            </>
          ) : (
            <>You're subscribed. Watch out for the next one.</>
          )}
        </p>
      </div>
    );
  }

  const isFooter = variant === "footer";

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${className}`} noValidate>
      <div className={isFooter ? "flex flex-col gap-2 sm:flex-row" : "space-y-3"}>
        <label htmlFor="newsletter-email" className="sr-only">
          Email address
        </label>
        <input
          id="newsletter-email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={status === "submitting"}
          className="w-full rounded-xl border border-rule bg-background px-4 py-2.5 text-sm text-foreground tracking-mono placeholder:text-muted-foreground/60 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="btn-brand inline-flex shrink-0 rounded-full items-center justify-center gap-2 px-5 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === "submitting" ? (
            <SpinnerGap size={18} weight="bold" className="animate-spin" />
          ) : (
            <PaperPlaneTilt size={18} weight="duotone" />
          )}
          Subscribe
        </button>
      </div>

      <HoneypotField value={honeypot} onChange={setHoneypot} />
      <Turnstile action="newsletter" onToken={setTurnstileToken} resetSignal={resetSignal} />

      {error && <FormError message={error} />}

      <p className="text-xs leading-relaxed text-muted-foreground">
        We'll email you a confirmation link first. Unsubscribe any time — see our{" "}
        <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-foreground">
          privacy policy
        </Link>
        .
      </p>
    </form>
  );
}
